import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RcTooltip from 'rc-tooltip';
import CropFreeRounded from '@mui/icons-material/CropFreeRounded';
import CropLandscapeRounded from '@mui/icons-material/CropLandscapeRounded';
import CropPortraitRounded from '@mui/icons-material/CropPortraitRounded';
import Crop32Rounded from '@mui/icons-material/Crop32Rounded';
import CropSquareRounded from '@mui/icons-material/CropSquareRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import {
  VIDEO_ASPECT_RATIOS,
  ASPECT_RATIO_VALUES,
} from '@src/constants/dubbing';
import {
  StyledRatioDropdown,
  StyledTriggerButton,
  StyledRatioMenu,
  StyledRatioMenuItem,
  RatioTooltipGlobalStyle,
} from './index.style';

const ICONS = {
  CropFreeRounded,
  CropLandscapeRounded,
  CropPortraitRounded,
  Crop32Rounded,
  CropSquareRounded,
};

const renderIcon = (iconName, className) => {
  const Icon = ICONS[iconName] || CropFreeRounded;
  return <Icon className={className} />;
};

const AspectRatioDropdown = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = useMemo(
    () =>
      VIDEO_ASPECT_RATIOS.find((option) => option.value === value) ||
      VIDEO_ASPECT_RATIOS[0],
    [value],
  );

  const triggerLabel = useMemo(() => {
    if (selectedOption.value === ASPECT_RATIO_VALUES.ORIGINAL) {
      return t(selectedOption.labelKey);
    }
    return selectedOption.labelKey;
  }, [selectedOption, value, t]);

  const handleSelect = useCallback(
    (option) => {
      onChange(option.value);
      setIsOpen(false);
    },
    [onChange],
  );

  const menuOverlay = (
    <StyledRatioMenu>
      {VIDEO_ASPECT_RATIOS.map((option) => {
        const isSelected = option.value === value;
        return (
          <StyledRatioMenuItem
            key={option.value}
            isSelected={isSelected}
            role="button"
            tabIndex={0}
            onClick={() => handleSelect(option)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelect(option);
              }
            }}
          >
            {renderIcon(option.iconName, 'ratio-item-icon')}
            <span className="ratio-item-label">
              {option.value === ASPECT_RATIO_VALUES.ORIGINAL
                ? t(option.labelKey)
                : option.labelKey}
            </span>
            {option.descriptionKey ? (
              <span className="ratio-item-description">
                {t(option.descriptionKey)}
              </span>
            ) : (
              <span className="ratio-item-description" />
            )}
            {isSelected ? <CheckRounded className="ratio-item-check" /> : null}
          </StyledRatioMenuItem>
        );
      })}
    </StyledRatioMenu>
  );

  return (
    <StyledRatioDropdown>
      <RatioTooltipGlobalStyle />
      <RcTooltip
        placement="bottomLeft"
        trigger={['click']}
        visible={isOpen && !disabled}
        onVisibleChange={(visible) => {
          if (disabled) return;
          setIsOpen(visible);
        }}
        overlay={menuOverlay}
        destroyTooltipOnHide
        overlayClassName="aspect-ratio-tooltip"
      >
        <StyledTriggerButton
          type="button"
          isActive={isOpen}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          {renderIcon(selectedOption.iconName, 'ratio-icon')}
          <span className="ratio-label">{triggerLabel}</span>
        </StyledTriggerButton>
      </RcTooltip>
    </StyledRatioDropdown>
  );
};

export default AspectRatioDropdown;
